/**
 * The rich editor's write, IN PLACE (review-task-11 finding 5). The editor's
 * only whole write is `setText`, which sets `textContent`: a pick or a prefill
 * then throws the caret to the END of the editor, flattens a real list into the
 * literal words "1. ...", and drops the undo stack. The words sent are the
 * same; how the editor LOOKS is not, and the founder's law is that only what
 * the composer knows changes on a task's screen, never how it looks.
 *
 * So the change is written the way typing writes: the span that differs between
 * the editor's words and the new ones (their common prefix and suffix set
 * aside) is mapped to a Range inside the contenteditable and replaced with the
 * browser's own insertText. The caret lands behind what was inserted, the
 * blocks around it are never touched, and the edit joins the undo stack.
 *
 * It answers FALSE whenever it is not sure, and the host then does the
 * whole write, which is always right about the words:
 *   - no execCommand (jsdom; a browser that finally drops it);
 *   - the span touches letters no text node holds: a list's generated "1. ",
 *     the newline between two blocks (a span crossing a block boundary), an
 *     empty line, an empty editor. One check covers them all: the Range's own
 *     text must BE the letters being replaced;
 *   - the new letters hold a line break (what a browser builds for one differs
 *     by engine and by block, so it is never guessed);
 *   - the command refuses, or ran and the editor does not read as the new words.
 *
 * PROVEN HERE (jsdom, execCommand stubbed): the Range's containers and offsets,
 * the command and the letters it is handed, every fallback. PROVEN ONLY IN A
 * REAL BROWSER (the flag-on drill: a mid-sentence pick, a pick inside a list):
 * that insertText really leaves the list a list, lands the caret behind the
 * handle and its space, fires the `input` the editor syncs from, and that
 * Cmd+Z takes the pick back.
 */
import { serializeComposer } from "@/components/home-briefing/rich-composer/serialize";

/** Chrome writes a space that would not show (a trailing one) as U+00A0. */
const plain = (s: string): string => s.replace(/ /g, " ");

/** Where a text node's letters start in the words as sent. Read through the
 *  serializer itself, never a second copy of its rules: the words up to the END
 *  of the node (which has letters, so nothing of it is trimmed), less its own. */
function startOf(root: HTMLElement, node: Text): number {
  const upTo = document.createRange();
  upTo.selectNodeContents(root);
  upTo.setEnd(node, node.length);
  const holder = document.createElement("div");
  holder.appendChild(upTo.cloneContents());
  return serializeComposer(holder).length - node.length;
}

type Spot = { node: Text; offset: number };

/** The text-node spots for two offsets into the words, or null when either is
 *  a letter no text node holds. The end prefers the start's own node. */
function spotsFor(
  root: HTMLElement,
  from: number,
  to: number
): { start: Spot; end: Spot } | null {
  let start: Spot | null = null;
  let end: Spot | null = null;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n && !(start && end); n = walker.nextNode()) {
    const node = n as Text;
    if (node.length === 0) continue;
    const base = startOf(root, node);
    const holds = (at: number) => at >= base && at <= base + node.length;
    // The LAST node that holds the start: between two text nodes of one line
    // the spot belongs to the one whose letters follow it.
    if (holds(from) && !end) start = { node, offset: from - base };
    if (start && holds(to) && to >= from)
      end = end ?? { node, offset: to - base };
  }
  return start && end ? { start, end } : null;
}

/** True = the editor now reads as `next`, written in place. False = nothing
 *  the host can rely on was written: do the whole write. */
export function writeWordsInPlace(root: HTMLElement, next: string): boolean {
  if (typeof document.execCommand !== "function") return false;
  const old = serializeComposer(root);
  if (plain(old) === plain(next)) return true;

  let head = 0;
  const most = Math.min(old.length, next.length);
  while (head < most && old[head] === next[head]) head += 1;
  let tail = 0;
  while (
    tail < most - head &&
    old[old.length - 1 - tail] === next[next.length - 1 - tail]
  )
    tail += 1;
  const removed = old.slice(head, old.length - tail);
  const inserted = next.slice(head, next.length - tail);
  if (inserted.includes("\n")) return false;

  const spots = spotsFor(root, head, old.length - tail);
  if (!spots) return false;
  const range = document.createRange();
  range.setStart(spots.start.node, spots.start.offset);
  range.setEnd(spots.end.node, spots.end.offset);
  // The one safety check: a span that touches a generated prefix or crosses a
  // block removes letters the Range does not hold.
  if (range.toString() !== removed) return false;

  const selection = window.getSelection();
  if (!selection) return false;
  root.focus();
  selection.removeAllRanges();
  selection.addRange(range);
  const ran =
    inserted === ""
      ? document.execCommand("delete")
      : document.execCommand("insertText", false, inserted);
  return ran && plain(serializeComposer(root)) === plain(next);
}
